import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/ui/logo';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ThemeToggle } from '@/components/theme-toggle';
import { useState, useEffect } from 'react';
import { useAdminPreview } from '@/lib/admin-preview-context';
import {
  Home,
  Music,
  Heart,
  User,
  Users,
  HelpCircle,
  Volume2,
  Waves,
  LayoutDashboard,
  LogOut,
  ChevronDown,
  Settings,
  Shield,
  BarChart2,
  Tag,
  FileText,
  Newspaper,
  Activity,
  Menu,
  X,
  Quote,
  Flag,
  Sparkles,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';

import { useProfile } from '@/lib/providers';
import { useTheme } from '@/components/theme-provider';
import { AdminRole, ContentType, ProfileFull } from '@/types/custom';
import { canSeeBinauralBeats, IconMap, IS_HARTZORG, ROUTES } from '@/lib/constants';
import { useKeywords } from '@/lib/hooks/use-keywords';
import { useTeamMeditations } from '@/lib/hooks/use-team-meditations';
import { useSamenMeditations } from '@/lib/hooks/use-samen-meditations';
import { Keyword } from '@/types/custom';

const ADMIN_LABEL = 'Admin';

function HnHIcon({ className }: { className?: string }) {
  return <img src="/images/hoofdnaarhart-icon.png" alt="" className={className} />;
}

const getMenuItems = (profile: ProfileFull | null, keywords: Keyword[], teamMeditationStatus?: 'active' | 'planned' | null, samenMeditationStatus?: 'active' | 'planned' | null, effectiveRole?: string) => {
  const keywordsSubmenu = keywords.map((keyword) => ({
    label: keyword.name.charAt(0).toUpperCase() + keyword.name.slice(1),
    path: `${ROUTES.MEDITATIONS}?query=${encodeURIComponent(keyword.name)}`,
  }));

  const isAdmin = profile?.role === AdminRole;
  const isEffectiveHoofdHartUser = effectiveRole === 'hoofdhartuser';
  const team = profile?.teams.at(profile.active_team_idx);
  const hasTeam = !!team && !isEffectiveHoofdHartUser;

  const menuItems = [
    { icon: Home, label: 'Home', path: '/' },
    {
      icon: IconMap.meditation,
      label: 'Meditaties',
      path: ROUTES.MEDITATIONS,
      submenu: [...keywordsSubmenu],
    },
    { icon: Activity, label: 'Oefeningen', path: '/oefeningen' },
    // Admin-only while the challenge is still being built: the texts are
    // drafts and the recordings are placeholders. Drop `isAdmin` to release it.
    ...(IS_HARTZORG && !isEffectiveHoofdHartUser && isAdmin
      ? [{ icon: Sparkles, label: '7-Daagse Challenge', path: ROUTES.CHALLENGE }]
      : []),
    { icon: IconMap.music, label: 'Muziek & Frequenties', path: '/muziek' },
    {
      icon: IconMap.nature_sound,
      label: 'Natuurgeluiden',
      path: ROUTES.NATURE_SOUNDS,
    },
    ...(canSeeBinauralBeats(isAdmin)
      ? [
          {
            icon: IconMap.binaural,
            label: 'Binaural Beats',
            path: ROUTES.BINAURAL_BEATS,
          },
        ]
      : []),
    {
      icon: LayoutDashboard,
      label: 'Inspiratiekaarten',
      path: '/inspiratiekaarten',
    },
    { icon: Heart, label: 'Favorieten', path: '/favorieten' },
    { icon: HelpCircle, label: 'Tips & Artikelen', path: ROUTES.TIPS },
    { icon: Newspaper, label: 'Nieuws', path: '/nieuws' },
    { icon: User, label: 'Profiel', path: '/profiel' },
    ...(hasTeam ? [{ icon: Users, label: 'Team', path: '/team', badge: teamMeditationStatus }] : []),
    ...(isEffectiveHoofdHartUser ? [{ icon: HnHIcon, label: 'Samen', path: '/samen', badge: samenMeditationStatus }] : []),

    ...(isAdmin
      ? [
          {
            icon: Shield,
            label: ADMIN_LABEL,
            path: null,
            submenu: [
              {
                icon: Users,
                label: isEffectiveHoofdHartUser ? 'HnH Gebruikers' : 'Gebruikers',
                path: '/admin/users',
              },
              {
                icon: LayoutDashboard,
                label: isEffectiveHoofdHartUser ? 'HnH Dashboard' : 'Dashboard',
                path: '/admin/dashboard',
              },
              {
                icon: Tag,
                label: isEffectiveHoofdHartUser ? 'HnH Keywords' : 'Keywords',
                path: '/admin/keywords',
              },
              {
                icon: FileText,
                label: isEffectiveHoofdHartUser ? 'HnH Tips & Artikelen' : 'Tips & Artikelen',
                path: '/admin/tips',
              },
              {
                icon: Home,
                label: 'Home',
                path: '/admin/home',
              },
              {
                icon: Quote,
                label: isEffectiveHoofdHartUser ? 'HnH Quotes' : 'Quotes',
                path: '/admin/quotes',
              },
              {
                icon: BarChart2,
                label: isEffectiveHoofdHartUser ? 'HnH Analytics' : 'Analytics',
                path: '/admin/analytics',
              },
              {
                icon: Settings,
                label: isEffectiveHoofdHartUser ? 'HnH Instellingen' : 'Settings',
                path: '/admin/settings',
              },
              {
                icon: Flag,
                label: 'Moderatie',
                path: '/admin/moderatie',
              },
            ],
          },
        ]
      : []),
  ];

  return menuItems;
};

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export function Sidebar({ isOpen = true, onClose }: SidebarProps) {
  const { profile, loadingProfile } = useProfile();
  const { isPreviewMode, togglePreviewMode, effectiveRole } = useAdminPreview();
  const { theme } = useTheme();
  const isAdmin = profile?.role === AdminRole;
  const isEffectiveHoofdHartUser = effectiveRole === 'hoofdhartuser';
  // default to expanding admin section (when present)
  const [expandedItems, setExpandedItems] = useState<string[]>([ADMIN_LABEL]);

  const { keywords, loading: loadingKeywords } = useKeywords([]);

  const team = profile?.teams.at(profile.active_team_idx);
  const { hasUpcoming, activeMeditation } = useTeamMeditations(team?.id);
  const teamMeditationStatus = activeMeditation ? 'active' as const : hasUpcoming ? 'planned' as const : null;

  const samen = profile?.samen;
  const { hasUpcoming: samenHasUpcoming, activeMeditation: samenActiveMeditation } = useSamenMeditations(samen?.id);
  const samenMeditationStatus = samenActiveMeditation ? 'active' as const : samenHasUpcoming ? 'planned' as const : null;

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  // Close sidebar when clicking a link on mobile
  const handleLinkClick = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile/Tablet overlay - visible below lg breakpoint (1024px) */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed lg:relative z-50 lg:z-auto
          w-64 h-screen border-r bg-background
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
      >
        {/* Mobile/Tablet close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 lg:hidden cap-top"
        >
          <X className="h-5 w-5" />
        </button>
      <ScrollArea className="h-full py-6">
        <div className="px-3 py-2">
          <div className="mb-6 px-4">
            <Logo
              className="w-32 h-32 mx-auto"
              linkTo="/"
              src={
                isEffectiveHoofdHartUser
                  ? theme === 'dark'
                    ? '/images/hoofdnaarhart-logo-white.png'
                    : '/images/hoofdnaarhart-logo.png'
                  : undefined
              }
            />
            {isAdmin && (
              <div className="flex justify-center mt-1">
                <button
                  onClick={togglePreviewMode}
                  className="text-muted-foreground hover:text-foreground transition-colors p-1"
                  title={isPreviewMode ? 'Terug naar admin weergave' : 'HoofdnaarHart weergave'}
                >
                  <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${isPreviewMode ? 'rotate-180' : '-rotate-90'}`} />
                </button>
              </div>
            )}
          </div>
          <div className="space-y-1">
            {getMenuItems(profile, keywords || [], teamMeditationStatus, samenMeditationStatus, effectiveRole).map((item, index) => {
              return (
                <div key={item.path}>
                  <Button
                    variant="ghost"
                    className="w-full justify-start hover:bg-green-50 dark:hover:bg-green-950/50 transition-all duration-300 ease-in-out"
                    onClick={() => {
                      if (item.submenu) {
                        setExpandedItems((prev) =>
                          prev.includes(item.label)
                            ? prev.filter((label) => label !== item.label)
                            : [...prev, item.label],
                        );
                      }
                    }}
                    asChild={!item.submenu}
                  >
                    {item.submenu ? (
                      <div className="flex items-center w-full">
                        {item.path ? (
                          <Link
                            to={item.path}
                            className="flex items-center flex-1 group"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <item.icon className="mr-2 h-4 w-4 group-hover:text-green-500 transition-colors duration-300" />
                            {item.label}
                          </Link>
                        ) : (
                          <div className="flex items-center flex-1 group">
                            <item.icon className="mr-2 h-4 w-4 group-hover:text-green-500 transition-colors duration-300" />
                            {item.label}
                          </div>
                        )}
                        <ChevronDown
                          className={`ml-auto h-4 w-4 transition-transform duration-300 ease-in-out ${expandedItems.includes(item.label) ? 'rotate-180' : ''}`}
                        />
                      </div>
                    ) : (
                      <Link to={item.path} className="group flex items-center" onClick={handleLinkClick}>
                        <item.icon className="mr-2 h-4 w-4 group-hover:text-green-500 transition-colors duration-300" />
                        {item.label}
                        {(item as any).badge && (
                          <span className={`ml-2 h-2.5 w-2.5 rounded-full ${item.label === 'Samen' ? 'bg-red-500' : 'bg-green-500'} ${(item as any).badge === 'active' ? 'animate-pulse' : ''}`} />
                        )}
                      </Link>
                    )}
                  </Button>

                  {item.submenu && expandedItems.includes(item.label) && (
                    <div className="ml-6 space-y-1 mt-1">
                      {item.submenu.map((subItem) => (
                        <div key={subItem.path} className="relative pl-4 group">
                          {subItem.icon ? (
                            <subItem.icon className="h-4 w-4 absolute left-0 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground group-hover:text-green-500 transition-all duration-300 ease-in-out" />
                          ) : (
                            // <subItem.icon className="mr-2 h-4 w-4 group-hover:text-green-500 transition-colors duration-300" />
                            <Heart className="absolute left-0 top-1/2 -translate-y-1/2 h-3 w-3 text-transparent group-hover:text-green-500 transition-all duration-300 ease-in-out" />
                          )}
                          <Button
                            variant="ghost"
                            className="w-full justify-start text-sm hover:bg-green-50 dark:hover:bg-green-950/50 transition-all duration-300 ease-in-out"
                            asChild
                          >
                            <Link to={subItem.path} onClick={handleLinkClick}>{subItem.label}</Link>
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            <Button
              variant="ghost"
              className="w-full justify-start text-red-500 hover:text-red-600 hover:bg-red-50"
              onClick={handleLogout}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
          <div className="px-4 mt-4 space-y-4">
            <ThemeToggle />
          </div>
        </div>
      </ScrollArea>
      </div>
    </>
  );
}

/**
 * Hamburger menu button for mobile/tablet
 * Breakpoints:
 * - Mobile: < 640px (sm)
 * - Tablet: 640px - 1024px (sm to lg)
 * - Desktop: >= 1024px (lg) - sidebar visible, hamburger hidden
 */
export function MobileMenuButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="fixed top-4 left-4 z-30 p-2 rounded-lg bg-background border shadow-sm hover:bg-gray-100 dark:hover:bg-gray-800 lg:hidden cap-top cap-hide-on-play"
      aria-label="Open menu"
    >
      <Menu className="h-6 w-6" />
    </button>
  );
}
